import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  Route, X, Loader2, MapPin, Navigation, Trash2, Plus, CheckCircle2, AlertTriangle, ExternalLink, ListChecks,
} from "lucide-react";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const km = (m) => (m == null ? "—" : `${(m / 1000).toFixed(1)} km`);

function FitLine({ pts }) {
  const map = useMap();
  useEffect(() => { if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40] }); else if (pts.length === 1) map.setView(pts[0], 13); }, [pts, map]);
  return null;
}

export function RunsPanel({ claimId }) {
  const [runs, setRuns] = useState([]);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [viewRun, setViewRun] = useState(null);

  const load = () => api.get(`/fieldclaim/runs?claim_id=${claimId}`).then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => { load(); }, [claimId]); // eslint-disable-line

  const del = async (id) => { if (!window.confirm("Delete this run?")) return; await api.delete(`/fieldclaim/runs/${id}`); load(); };

  return (
    <div className="border border-gold-500/20" data-testid="runs-panel">
      <div className="flex items-center justify-between px-4 py-3 bg-navy-900/60 border-b border-gold-500/15">
        <Mono className="text-[0.55rem] text-gold-400/80 flex items-center gap-1.5"><Route className="h-3 w-3" /> Inspection runs · {runs.length}</Mono>
        <button onClick={() => setPlannerOpen(true)} data-testid="plan-route-btn" className="inline-flex items-center gap-1.5 text-gold-400 hover:text-gold-300 text-xs"><Plus className="h-3.5 w-3.5" /> Plan route</button>
      </div>
      {runs.length === 0 ? (
        <div className="px-4 py-6 text-center text-white/35 text-xs font-light">No inspection runs yet. Plan a route from the contractor's finished Site IDs.</div>
      ) : (
        <div className="divide-y divide-gold-500/10">
          {runs.map((r) => {
            const done = r.stops.filter((s) => s.visited).length;
            return (
              <div key={r.id} className="px-4 py-3 flex items-center gap-3" data-testid={`run-${r.id}`}>
                <button onClick={() => setViewRun(r)} className="flex-1 min-w-0 text-left">
                  <div className="text-sm text-white truncate">{r.name}</div>
                  <Mono className="text-[0.5rem] text-white/40">{r.stops.length} stops · {km(r.distance_m)} · {done}/{r.stops.length} done{r.unlocated?.length ? ` · ${r.unlocated.length} unlocated` : ""}</Mono>
                </button>
                <span className={`font-mono text-[0.5rem] px-1.5 py-0.5 border ${r.status === "done" ? "text-emerald-300 border-emerald-500/40" : "text-gold-300 border-gold-500/40"}`}>{r.status}</span>
                <button onClick={() => del(r.id)} data-testid={`delete-run-${r.id}`} className="text-white/25 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>
      )}
      {plannerOpen && <PlannerModal claimId={claimId} onClose={() => setPlannerOpen(false)} onSaved={(run) => { setPlannerOpen(false); load(); setViewRun(run); }} />}
      {viewRun && <RunDetailModal run={viewRun} onClose={() => setViewRun(null)} onChange={load} />}
    </div>
  );
}

function PlannerModal({ claimId, onClose, onSaved }) {
  const [ids, setIds] = useState("");
  const [name, setName] = useState("");
  const [resolved, setResolved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loc, setLoc] = useState({});

  const resolve = async () => {
    const list = ids.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    try { const { data } = await api.post("/fieldclaim/runs/resolve", { claim_id: claimId, ids: list }); setResolved(data); }
    finally { setBusy(false); }
  };

  const setAssetLoc = async (assetId) => {
    const v = loc[assetId] || {};
    if (v.lat === "" || v.lng === "" || v.lat == null) return;
    await api.patch(`/fieldclaim/assets/${assetId}`, { latitude: Number(v.lat), longitude: Number(v.lng) });
    resolve();
  };

  const save = async () => {
    if (!resolved) return;
    setBusy(true);
    try {
      const { data } = await api.post("/fieldclaim/runs", {
        name: name || `Inspection run — ${new Date().toLocaleDateString("en-AU")}`,
        claim_id: claimId, asset_ids: resolved.matched.map((m) => m.asset_id),
      });
      onSaved(data);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-lg p-8 space-y-5 max-h-[92vh] overflow-y-auto" data-testid="planner-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl text-white flex items-center gap-2"><Route className="h-5 w-5 text-gold-400" /> Plan inspection run</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <p className="text-white/55 text-sm font-light">Paste the Site IDs (or Road IDs) the contractor gave you — one per line or comma-separated. The Office will match them and build the most efficient route.</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Run name (optional)" data-testid="run-name-input" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500" />
        <textarea value={ids} onChange={(e) => setIds(e.target.value)} rows={4} placeholder={"S1\nS2\nS3"} data-testid="run-ids-input" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white text-sm font-mono focus:outline-none focus:border-gold-500 resize-none" />
        <button onClick={resolve} disabled={busy} data-testid="resolve-ids-btn" className="w-full border border-gold-500/40 text-gold-300 py-2.5 hover:bg-gold-500/10 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} Resolve site IDs</button>

        {resolved && (
          <div className="space-y-3" data-testid="resolve-result">
            <div className="flex items-center gap-4 text-xs font-mono">
              <span className="text-emerald-300">{resolved.located} located</span>
              {resolved.unlocated > 0 && <span className="text-amber-300">{resolved.unlocated} need location</span>}
              {resolved.unmatched.length > 0 && <span className="text-red-300">{resolved.unmatched.length} not found</span>}
            </div>
            <div className="border border-gold-500/15 divide-y divide-gold-500/10 max-h-52 overflow-y-auto">
              {resolved.matched.map((m) => (
                <div key={m.asset_id} className="px-3 py-2 text-sm" data-testid={`resolved-${m.asset_id}`}>
                  <div className="flex items-center gap-2">
                    {m.located ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />}
                    <span className="text-white truncate flex-1">{m.name} <span className="text-white/40 font-mono text-[0.6rem]">{m.site_number || m.road_id}</span></span>
                  </div>
                  {!m.located && (
                    <div className="flex items-center gap-2 mt-2 pl-5">
                      <input placeholder="lat" onChange={(e) => setLoc((l) => ({ ...l, [m.asset_id]: { ...l[m.asset_id], lat: e.target.value } }))} data-testid={`loc-lat-${m.asset_id}`} className="w-24 bg-navy-950 border border-gold-500/20 px-2 py-1 text-white text-xs focus:outline-none focus:border-gold-500" />
                      <input placeholder="lng" onChange={(e) => setLoc((l) => ({ ...l, [m.asset_id]: { ...l[m.asset_id], lng: e.target.value } }))} data-testid={`loc-lng-${m.asset_id}`} className="w-24 bg-navy-950 border border-gold-500/20 px-2 py-1 text-white text-xs focus:outline-none focus:border-gold-500" />
                      <button onClick={() => setAssetLoc(m.asset_id)} data-testid={`set-loc-${m.asset_id}`} className="text-gold-300 hover:text-gold-200 text-xs">Set</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {resolved.unmatched.length > 0 && <div className="text-red-300/80 text-xs" data-testid="unmatched-list">Not found: {resolved.unmatched.join(", ")}</div>}
            <button onClick={save} disabled={busy || resolved.located === 0} data-testid="save-run-btn" className="w-full bg-gold-500 text-navy-950 font-semibold py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Route className="h-4 w-4" />} Optimise &amp; save route ({resolved.located} stops)</button>
          </div>
        )}
      </div>
    </div>
  );
}

function RunDetailModal({ run: initial, onClose, onChange }) {
  const [run, setRun] = useState(initial);
  const line = (run.geometry || []).map((p) => [p[0], p[1]]);
  const stopPts = run.stops.map((s) => [s.latitude, s.longitude]);
  const allPts = line.length ? line : stopPts;

  const toggleVisited = async (assetId, visited) => {
    const { data } = await api.patch(`/fieldclaim/runs/${run.id}`, { visited_asset_id: assetId, visited });
    setRun(data); onChange();
  };

  return (
    <div className="fixed inset-0 bg-navy-950/80 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="run-detail-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold-500/20">
          <div>
            <Mono className="text-gold-400 text-[0.55rem]">Inspection run · {run.provider} route</Mono>
            <h2 className="font-heading text-xl text-white">{run.name}</h2>
            <Mono className="text-[0.55rem] text-white/45">{run.stops.length} stops · {km(run.distance_m)}</Mono>
          </div>
          <div className="flex items-center gap-3">
            {run.gmaps_url && <a href={run.gmaps_url} target="_blank" rel="noreferrer" data-testid="run-gmaps-link" className="inline-flex items-center gap-1.5 border border-gold-500/40 text-gold-300 hover:bg-gold-500/10 px-3 py-2 text-xs transition-colors"><Navigation className="h-3.5 w-3.5" /> Google Maps <ExternalLink className="h-3 w-3" /></a>}
            <button onClick={onClose}><X className="h-5 w-5 text-white/60" /></button>
          </div>
        </div>
        {allPts.length > 0 && (
          <div style={{ height: "300px" }}>
            <MapContainer center={allPts[0]} zoom={11} style={{ height: "100%", width: "100%", background: "#0b1220" }} scrollWheelZoom>
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitLine pts={allPts} />
              {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#d4a72c", weight: 4, opacity: 0.85 }} />}
              {run.stops.map((s) => (
                <CircleMarker key={s.asset_id} center={[s.latitude, s.longitude]} radius={11} pathOptions={{ color: s.visited ? "#34d399" : "#d4a72c", fillColor: s.visited ? "#34d399" : "#e9c46a", fillOpacity: 0.9, weight: 3 }}>
                  <Popup><b>#{s.seq} {s.name}</b><br />{s.site_number || s.road_id}</Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        )}
        <div className="p-6 space-y-2">
          {run.stops.map((s) => (
            <div key={s.asset_id} className="flex items-center gap-3 border border-gold-500/15 px-4 py-2.5" data-testid={`run-stop-${s.asset_id}`}>
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-mono shrink-0 ${s.visited ? "bg-emerald-500 text-navy-950" : "bg-gold-500 text-navy-950"}`}>{s.seq}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{s.name}</div>
                <Mono className="text-[0.5rem] text-white/40">{s.site_number || s.road_id} · {Number(s.latitude).toFixed(4)}, {Number(s.longitude).toFixed(4)}</Mono>
              </div>
              <button onClick={() => toggleVisited(s.asset_id, !s.visited)} data-testid={`visit-${s.asset_id}`} className={`text-xs px-2.5 py-1 border transition-colors ${s.visited ? "border-emerald-500/50 text-emerald-300" : "border-gold-500/40 text-gold-300 hover:bg-gold-500/10"}`}>{s.visited ? "Visited ✓" : "Mark visited"}</button>
            </div>
          ))}
          {run.unlocated?.length > 0 && (
            <div className="text-amber-300/80 text-xs pt-2" data-testid="run-unlocated"><AlertTriangle className="h-3.5 w-3.5 inline mr-1" /> Not routed (no location): {run.unlocated.map((u) => u.name).join(", ")}</div>
          )}
        </div>
      </div>
    </div>
  );
}
